/**
 * GameReplayManager - Complete Match Recording, Playback Engine, and Sharing System
 * Captures move-by-move game events (deals, discards, starter cuts, pegging plays,
 * shows, obstacles, and scores) for full interactive playback, export, and link sharing.
 */

import { appStorage } from '../storage/IndexedDBStorage.js';
import { Profile } from '../profile/Profile.js';

export const REPLAY_STORAGE_KEY = 'crazyCribbage.replays.v1';
export const AUTO_RECORD_KEY = 'crazyCribbage.autoRecord.v1';
export const MAX_SAVED_REPLAYS = 30;

let _memoryFallbackStorage = {};

function getStorage() {
  if (typeof window !== 'undefined' && window.appStorage) return window.appStorage;
  if (appStorage && typeof appStorage.getItem === 'function') return appStorage;
  try {
    if (typeof localStorage !== 'undefined' && typeof localStorage.getItem === 'function') {
      localStorage.getItem('__storage_test__');
      return localStorage;
    }
  } catch (_) {}
  return {
    getItem: (key) => (_memoryFallbackStorage[key] !== undefined ? _memoryFallbackStorage[key] : null),
    setItem: (key, val) => { _memoryFallbackStorage[key] = String(val); },
    removeItem: (key) => { delete _memoryFallbackStorage[key]; },
    clear: () => { _memoryFallbackStorage = {}; }
  };
}

export class GameReplayManager {
  static currentReplay = null;
  static lastRecordedReplay = null;
  static _activeRound = 1;
  static _activeHands = {};
  static _activeStarter = null;
  static _activeCrib = [];
  static _activePile = [];
  static _activeScores = { p1: 0, p2: 0 };
  static _activeCount = 0;

  /**
   * Checks whether automatic match recording is enabled.
   * @returns {boolean}
   */
  static isAutoRecordEnabled() {
    try {
      const storage = getStorage();
      if (!storage) return true;
      const val = storage.getItem(AUTO_RECORD_KEY);
      return val === null || val === 'true' || val === true;
    } catch (_) {
      return true;
    }
  }

  /**
   * Sets auto-record preference.
   * @param {boolean} enabled
   */
  static setAutoRecordEnabled(enabled) {
    try {
      const storage = getStorage();
      if (storage) {
        storage.setItem(AUTO_RECORD_KEY, enabled ? 'true' : 'false');
      }
    } catch (_) {}
  }

  /**
   * Initializes and begins recording a new game session.
   * @param {Object} options
   */
  static startRecording({ settings = {}, state = {}, brand = 'crazy', pName = null } = {}) {
    const timestamp = Date.now();
    const date = new Date(timestamp);
    const dateLabel = date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });

    let p1Name = 'You';
    let p2Name = 'Computer';

    if (typeof pName === 'function') {
      p1Name = pName('p1') || 'You';
      p2Name = pName('p2') || 'Computer';
    } else if (typeof pName === 'string' && pName.trim()) {
      p1Name = pName.trim();
    } else if (state && state.pName) {
      p1Name = state.pName;
    }

    if (state && state.cpuDifficulty) {
      const diffLabels = { turtler: 'The Turtler', shark: 'The Shark', gambler: 'The High Roller' };
      p2Name = diffLabels[state.cpuDifficulty] || 'Computer';
    }
    if (state && state.players && state.players.p1 && state.players.p1.name) p1Name = state.players.p1.name;
    if (state && state.players && state.players.p2 && state.players.p2.name) p2Name = state.players.p2.name;

    const resolvedBrand = brand || (typeof window !== 'undefined' ? (window.getAppBrand ? window.getAppBrand() : (window.currentBrand || 'crazy')) : 'crazy');

    const brandNames = {
      crazy: 'Crazy Cribbage',
      clash: 'Cribbage Clash',
      pints: 'Pints & Pegs',
      adult: 'Skunked & Stripped'
    };

    let p1Avatar = '🤠';
    try {
      const prof = (typeof window !== 'undefined' && window.Profile)
        ? window.Profile.getInstance()
        : (Profile ? Profile.getInstance() : null);
      if (prof) {
        if (p1Name === 'You' && prof.getName()) p1Name = prof.getName();
        const avObj = prof.getAvatarObject ? prof.getAvatarObject() : null;
        if (avObj && avObj.emoji) p1Avatar = avObj.emoji;
      }
    } catch (_) {}

    GameReplayManager._activeRound = 1;
    GameReplayManager._activeHands = {};
    GameReplayManager._activeStarter = null;
    GameReplayManager._activeCrib = [];
    GameReplayManager._activePile = [];
    GameReplayManager._activeScores = { p1: 0, p2: 0 };
    GameReplayManager._activeCount = 0;

    GameReplayManager.currentReplay = {
      version: '1.0.0',
      id: `replay_${timestamp}_${Math.random().toString(36).substring(2, 7)}`,
      timestamp,
      dateLabel,
      brand: resolvedBrand || 'crazy',
      brandLabel: brandNames[resolvedBrand] || 'Crazy Cribbage',
      target: Number(settings.target) || 121,
      goal: settings.goal || 'high',
      board3DId: settings.selected3DBoardId || 'us-flag-250',
      boardViewMode: settings.boardViewMode || '3d',
      players: {
        p1: { id: 'p1', name: p1Name, avatar: p1Avatar, isHuman: true },
        p2: { id: 'p2', name: p2Name, avatar: '🤖', isHuman: false }
      },
      winner: null,
      winnerName: null,
      finalScores: { p1: 0, p2: 0 },
      skunkKind: null,
      settings: { ...settings },
      events: []
    };

    // Emit initial game start event
    GameReplayManager.recordEvent('game_start', {
      narration: `Match started: ${p1Name} vs ${p2Name} (${brandNames[brand] || 'Crazy Cribbage'}, First to ${settings.target || 121} pts).`,
      dealer: state.dealer || 'p1',
      activePlayer: state.dealer || 'p1',
      scores: { p1: 0, p2: 0 }
    });

    return GameReplayManager.currentReplay;
  }

  /**
   * Records a discrete match event into the current replay timeline.
   * @param {string} type - Event type identifier
   * @param {Object} data - Payload data
   */
  static recordEvent(type, data = {}) {
    if (!GameReplayManager.currentReplay) return;

    const events = GameReplayManager.currentReplay.events;
    const step = events.length;
    if (data.round) {
      GameReplayManager._activeRound = data.round;
    }
    const round = data.round || GameReplayManager._activeRound || 1;

    // Track state updates
    if (data.scores) {
      GameReplayManager._activeScores = {
        p1: data.scores.p1 !== undefined ? data.scores.p1 : (data.scores.you || GameReplayManager._activeScores.p1 || 0),
        p2: data.scores.p2 !== undefined ? data.scores.p2 : (data.scores.cpu || GameReplayManager._activeScores.p2 || 0)
      };
    }

    if (data.countAfter !== undefined) {
      GameReplayManager._activeCount = data.countAfter;
    } else if (data.count !== undefined) {
      GameReplayManager._activeCount = data.count;
    }

    if (data.hands) {
      GameReplayManager._activeHands = JSON.parse(JSON.stringify(data.hands));
    }

    if (data.cards) {
      if (type === 'show_hand' && data.who) {
        const whoKey = (data.who === 'you' || data.who === 'p1') ? 'p1' : 'p2';
        GameReplayManager._activeHands[whoKey] = [...data.cards];
      } else if (type === 'show_crib') {
        GameReplayManager._activeCrib = [...data.cards];
      }
    }

    if (data.starter) {
      GameReplayManager._activeStarter = { ...data.starter };
    }

    if (data.crib) {
      GameReplayManager._activeCrib = Array.isArray(data.crib) ? [...data.crib] : [];
    }

    if (data.pile) {
      GameReplayManager._activePile = Array.isArray(data.pile) ? [...data.pile] : [];
    } else if (data.card && type === 'peg_play') {
      GameReplayManager._activePile.push({ ...data.card });
      // Remove played card from the player's active hand
      if (data.who) {
        const whoKey = (data.who === 'you' || data.who === 'p1') ? 'p1' : 'p2';
        if (Array.isArray(GameReplayManager._activeHands[whoKey])) {
          const idx = GameReplayManager._activeHands[whoKey].findIndex(c =>
            (c.id && data.card.id && c.id === data.card.id) ||
            ((c.r === data.card.r || c.rank === data.card.rank) && (c.s === data.card.s || c.suit === data.card.suit))
          );
          if (idx !== -1) {
            GameReplayManager._activeHands[whoKey].splice(idx, 1);
          }
        }
      }
    } else if (type === 'discard' && data.who) {
      const whoKey = (data.who === 'you' || data.who === 'p1') ? 'p1' : 'p2';
      const discardList = Array.isArray(data.discards) ? data.discards : (Array.isArray(data.cards) ? data.cards : []);
      if (Array.isArray(GameReplayManager._activeHands[whoKey])) {
        for (const dc of discardList) {
          const idx = GameReplayManager._activeHands[whoKey].findIndex(c =>
            (c.id && dc.id && c.id === dc.id) ||
            ((c.r === dc.r || c.rank === dc.rank) && (c.s === dc.s || c.suit === dc.suit))
          );
          if (idx !== -1) {
            GameReplayManager._activeHands[whoKey].splice(idx, 1);
          }
        }
      }
    } else if (type === 'round_start') {
      GameReplayManager._activeRound = round;
      GameReplayManager._activePile = [];
      GameReplayManager._activeCount = 0;
      GameReplayManager._activeCrib = [];
      GameReplayManager._activeStarter = null;
      GameReplayManager._activeHands = { p1: [], p2: [] };
    } else if (type === 'peg_go' || type === 'peg_reset' || type === 'show_hand' || type === 'show_crib') {
      GameReplayManager._activePile = [];
      GameReplayManager._activeCount = 0;
    }


    // Generate descriptive narration if not explicitly supplied
    let narration = data.narration;
    if (!narration) {
      const p1 = GameReplayManager.currentReplay?.players?.p1?.name || 'You';
      const p2 = GameReplayManager.currentReplay?.players?.p2?.name || 'Computer';
      const whoName = (data.who === 'p1' || data.who === 'you') ? p1 : p2;

      switch (type) {
        case 'round_start':
          narration = `Round ${round} began. Dealer: ${whoName}.`;
          break;
        case 'deal':
          narration = `Cards dealt for Round ${round}.`;
          break;
        case 'discard':
          narration = `${whoName} discarded to the crib.`;
          break;
        case 'starter_cut': {
          const c = data.starter;
          const suitSyms = { H: '♥', D: '♦', S: '♠', C: '♣' };
          const cardStr = c ? `${c.r}${suitSyms[c.s] || c.s}` : 'Starter Card';
          narration = `Starter cut: ${cardStr}${data.heels ? ' (His Heels! +2 points for dealer)' : ''}.`;
          break;
        }
        case 'peg_play': {
          const c = data.card;
          const suitSyms = { H: '♥', D: '♦', S: '♠', C: '♣' };
          const cardStr = c ? `${c.r}${suitSyms[c.s] || c.s}` : 'Card';
          narration = `${whoName} played ${cardStr} — Count: ${data.countAfter ?? data.count}${data.points ? ` (+${data.points} pts: ${data.reason || 'score'})` : ''}.`;
          break;
        }
        case 'peg_go':
          narration = data.reason === '31'
            ? `${whoName} reached 31 (+${data.points || 2} pts). Sequence cleared.`
            : `${whoName} scored Go (+${data.points || 1} pt). Sequence cleared.`;
          break;
        case 'peg_reset':
          narration = `Count reset to 0 (${data.reason || '31 or Go reached'}). Sequence cleared.`;
          break;
        case 'show_hand': {
          let comboText = '';
          if (Array.isArray(data.combos) && data.combos.length) {
            comboText = data.combos.map(c => typeof c === 'string' ? c : (c.why ? `${c.why} (+${c.n})` : (c.name || c.text || ''))).filter(Boolean).join(', ');
          }
          narration = `${whoName} scored ${data.points || 0} pts in hand${comboText ? ` (${comboText})` : ''}.`;
          break;
        }
        case 'show_crib': {
          let comboText = '';
          if (Array.isArray(data.combos) && data.combos.length) {
            comboText = data.combos.map(c => typeof c === 'string' ? c : (c.why ? `${c.why} (+${c.n})` : (c.name || c.text || ''))).filter(Boolean).join(', ');
          }
          narration = `${whoName} scored ${data.points || 0} pts in crib${comboText ? ` (${comboText})` : ''}.`;
          break;
        }
        case 'obstacle':
          narration = `Hazard triggered: ${whoName} landed on ${data.name || 'Obstacle'} (${data.action || ''}).`;
          break;
        case 'game_over':
          narration = `Game Over! ${data.winnerName || whoName} won with ${data.scores?.[data.winner] || data.finalScores?.[data.winner] || 121} points!`;
          break;
        default:
          narration = data.text || 'Match event occurred.';
      }
    }

    const eventSnapshot = {
      step,
      round,
      type,
      narration,
      who: data.who || null,
      card: data.card ? { ...data.card } : null,
      starter: data.starter ? { ...data.starter } : (GameReplayManager._activeStarter ? { ...GameReplayManager._activeStarter } : null),
      count: GameReplayManager._activeCount,
      scores: { ...GameReplayManager._activeScores },
      pile: [...GameReplayManager._activePile],
      crib: [...GameReplayManager._activeCrib],
      hands: JSON.parse(JSON.stringify(GameReplayManager._activeHands)),
      cards: data.cards ? [...data.cards] : null,
      combos: Array.isArray(data.combos) ? [...data.combos] : null,
      points: data.points || 0,
      reason: data.reason || null,
      details: data.details || null
    };

    events.push(eventSnapshot);

    if (type === 'peg_play' && (data.countAfter === 31 || data.count === 31)) {
      GameReplayManager._activePile = [];
      GameReplayManager._activeCount = 0;
    }

    if (type === 'round_start') {
      GameReplayManager._activeRound = round;
    }
  }

  /**
   * Finalizes the current match recording and persists it to storage.
   * @param {string} winner
   * @param {Object} finalScores
   * @param {string} [skunkKind]
   */
  static finishRecording(winner, finalScores = {}, skunkKind = null) {
    if (!GameReplayManager.currentReplay) {
      return GameReplayManager.lastRecordedReplay || null;
    }

    const rep = GameReplayManager.currentReplay;
    if (!rep) return null;

    rep.winner = winner || 'p1';
    const p1 = rep.players?.p1?.name || 'You';
    const p2 = rep.players?.p2?.name || 'Computer';
    rep.winnerName = (winner === 'p1' || winner === 'you') ? p1 : p2;
    rep.finalScores = {
      p1: finalScores.p1 !== undefined ? finalScores.p1 : (finalScores.you || GameReplayManager._activeScores.p1 || 0),
      p2: finalScores.p2 !== undefined ? finalScores.p2 : (finalScores.cpu || GameReplayManager._activeScores.p2 || 0)
    };
    rep.skunkKind = skunkKind || null;
    rep.totalRounds = GameReplayManager._activeRound || 1;

    // Record terminal game over event
    GameReplayManager.recordEvent('game_over', {
      winner: rep.winner,
      winnerName: rep.winnerName,
      scores: rep.finalScores,
      skunkKind: rep.skunkKind,
      narration: `Game Over! ${rep.winnerName} won the match (${rep.finalScores.p1}–${rep.finalScores.p2})!`
    });

    GameReplayManager.lastRecordedReplay = rep;

    // Auto-save if enabled
    if (GameReplayManager.isAutoRecordEnabled()) {
      GameReplayManager.saveReplay(rep);
    }

    GameReplayManager.currentReplay = null;
    return rep;
  }

  /**
   * Saves a replay object to persistent storage.
   * @param {Object} replay
   */
  static saveReplay(replay, storage = null) {
    if (!replay || !replay.id) return;
    if (!Array.isArray(replay.events) || replay.events.length <= 2) return;
    try {
      const s = storage || getStorage();
      const list = GameReplayManager.loadReplays(s);
      // Filter out existing copy with matching ID
      const filtered = list.filter(r => r.id !== replay.id);
      filtered.unshift(replay);
      // Keep within max saved limit
      const trimmed = filtered.slice(0, MAX_SAVED_REPLAYS);
      const str = JSON.stringify(trimmed);
      if (s) {
        s.setItem(REPLAY_STORAGE_KEY, str);
      }
      if (typeof window !== 'undefined' && window.localStorage && s !== window.localStorage) {
        try {
          window.localStorage.setItem(REPLAY_STORAGE_KEY, str);
        } catch (_) {}
      }

      // Sync into player profile separated by brand
      try {
        const prof = (typeof window !== 'undefined' && window.Profile)
          ? window.Profile.getInstance(s)
          : (Profile ? Profile.getInstance(s) : null);
        if (prof && typeof prof.recordReplay === 'function') {
          prof.recordReplay(replay, s);
        }
      } catch (_) {}
    } catch (err) {
      console.warn('[GameReplayManager] Failed to persist replay:', err);
    }
  }

  /**
   * Loads all saved replays from storage (newest first).
   * @param {Object} [storage=null]
   * @returns {Array<Object>}
   */
  static loadReplays(storage = null) {
    try {
      const s = storage || getStorage();
      let raw = s ? s.getItem(REPLAY_STORAGE_KEY) : null;
      if (!raw && typeof window !== 'undefined' && window.localStorage && s !== window.localStorage) {
        try {
          raw = window.localStorage.getItem(REPLAY_STORAGE_KEY);
        } catch (_) {}
      }
      let list = [];
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) list = parsed;
      }

      // Synchronize with user profile replays
      try {
        const prof = (typeof window !== 'undefined' && window.Profile)
          ? window.Profile.getInstance(s)
          : (Profile ? Profile.getInstance(s) : null);
        if (prof && typeof prof.getReplays === 'function') {
          const pReps = prof.getReplays(s);
          if (pReps && Array.isArray(pReps.list) && pReps.list.length > 0) {
            const map = new Map();
            for (const r of list) {
              if (r && r.id) map.set(r.id, r);
            }
            for (const r of pReps.list) {
              if (r && r.id && !map.has(r.id)) map.set(r.id, r);
            }
            list = Array.from(map.values()).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
          }
        }
      } catch (_) {}

      return list;
    } catch (err) {
      console.warn('[GameReplayManager] Error parsing saved replays:', err);
      return [];
    }
  }

  /**
   * Retrieves a single replay by its unique ID.
   * @param {string} id
   * @param {Object} [storage=null]
   * @returns {Object|null}
   */
  static getReplayById(id, storage = null) {
    if (!id) return null;
    const all = GameReplayManager.loadReplays(storage);
    const found = all.find(r => r.id === id);
    if (found) return found;
    if (GameReplayManager.lastRecordedReplay && GameReplayManager.lastRecordedReplay.id === id) {
      return GameReplayManager.lastRecordedReplay;
    }
    return null;
  }

  /**
   * Retrieves saved replays separated by brand.
   * @param {string} [brand='all']
   * @param {Object} [storage=null]
   * @returns {Array<Object>}
   */
  static getReplaysByBrand(brand = 'all', storage = null) {
    const all = GameReplayManager.loadReplays(storage);
    if (!brand || brand === 'all') return all;
    return all.filter(r => (r.brand || 'crazy') === brand);
  }

  /**
   * Retrieves summary counts of saved replays for each brand and overall.
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  static getBrandSummary(storage = null) {
    const all = GameReplayManager.loadReplays(storage);
    return {
      all: all.length,
      crazy: all.filter(r => (r.brand || 'crazy') === 'crazy').length,
      clash: all.filter(r => (r.brand || 'crazy') === 'clash').length,
      pints: all.filter(r => (r.brand || 'crazy') === 'pints').length,
      adult: all.filter(r => (r.brand || 'crazy') === 'adult').length
    };
  }

  /**
   * Deletes a replay by ID.
   * @param {string} id
   * @param {Object} [storage=null]
   */
  static deleteReplay(id, storage = null) {
    if (!id) return;
    if (GameReplayManager.lastRecordedReplay && GameReplayManager.lastRecordedReplay.id === id) {
      GameReplayManager.lastRecordedReplay = null;
    }
    try {
      const s = storage || getStorage();
      const list = GameReplayManager.loadReplays(s).filter(r => r.id !== id);
      if (s) {
        s.setItem(REPLAY_STORAGE_KEY, JSON.stringify(list));
      }
      try {
        const prof = (typeof window !== 'undefined' && window.Profile)
          ? window.Profile.getInstance(s)
          : (Profile ? Profile.getInstance(s) : null);
        if (prof && typeof prof.deleteReplay === 'function') {
          prof.deleteReplay(id, s);
        }
      } catch (_) {}
    } catch (err) {}
  }

  /**
   * Clears all saved replays.
   * @param {Object} [storage=null]
   */
  static clearAllReplays(storage = null) {
    try {
      const s = storage || getStorage();
      if (s) s.removeItem(REPLAY_STORAGE_KEY);
      try {
        const prof = (typeof window !== 'undefined' && window.Profile)
          ? window.Profile.getInstance(s)
          : (Profile ? Profile.getInstance(s) : null);
        if (prof && typeof prof.clearReplays === 'function') {
          prof.clearReplays('all', s);
        }
      } catch (_) {}
    } catch (_) {}
  }

  /**
   * Triggers a browser file download of the replay as a `.cribbage.json` file.
   * @param {Object|string} replayOrId
   */
  static exportReplayFile(replayOrId) {
    const replay = typeof replayOrId === 'string' ? GameReplayManager.getReplayById(replayOrId) : replayOrId;
    if (!replay) {
      console.warn('[GameReplayManager] No replay available to export.');
      return;
    }

    try {
      const json = JSON.stringify(replay, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cleanDate = (replay.id || 'game').replace(/[^a-zA-Z0-9_-]/g, '_');
      a.download = `crazy-cribbage-${cleanDate}.cribbage.json`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 300);
    } catch (err) {
      console.error('[GameReplayManager] Export failed:', err);
    }
  }

  /**
   * Reads and parses a `.json` or `.cribbage.json` file uploaded by the user.
   * @param {File} file
   * @returns {Promise<Object>}
   */
  static importReplayFile(file) {
    return new Promise((resolve, reject) => {
      if (!file) return reject(new Error('No file provided.'));
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const replay = JSON.parse(e.target.result);
          if (!replay || !Array.isArray(replay.events)) {
            throw new Error('Invalid replay format: missing events timeline.');
          }
          // Ensure unique ID
          if (!replay.id) {
            replay.id = `replay_${Date.now()}_imported`;
          }
          GameReplayManager.saveReplay(replay);
          resolve(replay);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file.'));
      reader.readAsText(file);
    });
  }

  /**
   * Compresses a replay into a URL-safe Base64 string for link sharing.
   * @param {Object} replay
   * @returns {Promise<string>}
   */
  static async compressReplay(replay) {
    if (!replay) return '';
    const jsonStr = JSON.stringify(replay);

    if (typeof CompressionStream !== 'undefined') {
      try {
        const stream = new Blob([jsonStr]).stream();
        const compressedStream = stream.pipeThrough(new CompressionStream('deflate-raw'));
        const response = new Response(compressedStream);
        const buffer = await response.arrayBuffer();
        let binary = '';
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      } catch (_) {}
    }

    // Fallback: URI-encoded base64
    try {
      return btoa(unescape(encodeURIComponent(jsonStr))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (_) {
      return encodeURIComponent(jsonStr);
    }
  }

  /**
   * Decompresses a URL parameter string back into a replay object.
   * @param {string} compressedStr
   * @returns {Promise<Object|null>}
   */
  static async decompressReplay(compressedStr) {
    if (!compressedStr) return null;
    let b64 = decodeURIComponent(compressedStr).replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';

    if (typeof DecompressionStream !== 'undefined') {
      try {
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        const stream = new Blob([bytes]).stream();
        const decompressedStream = stream.pipeThrough(new DecompressionStream('deflate-raw'));
        const response = new Response(decompressedStream);
        const text = await response.text();
        return JSON.parse(text);
      } catch (_) {}
    }

    // Fallback
    try {
      const decoded = decodeURIComponent(escape(atob(b64)));
      return JSON.parse(decoded);
    } catch (_) {
      try {
        return JSON.parse(decodeURIComponent(compressedStr));
      } catch (_) {
        return null;
      }
    }
  }

  /**
   * Generates a full shareable web URL for this replay.
   * @param {Object} replay
   * @returns {Promise<string>}
   */
  static async generateShareUrl(replay) {
    const compressed = await GameReplayManager.compressReplay(replay);
    if (!compressed) return '';
    const base = typeof window !== 'undefined' && window.location ? window.location.href.split('?')[0].split('#')[0] : 'https://crazycribbage.com';
    return `${base}?replay=${compressed}`;
  }

  static _toastTimer = null;

  /**
   * Prominent floating toast notification rendered on top of all modals/overlays (z-index: 99999).
   * @param {string} msg
   * @param {number} [duration=3000]
   */
  static showToast(msg, duration = 3000) {
    if (typeof document === 'undefined') return;
    let toast = document.getElementById('replayToast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'replayToast';
      toast.style.cssText = `
        position: fixed;
        bottom: 32px;
        left: 50%;
        transform: translateX(-50%) translateY(20px);
        background: #1e1505;
        border: 1.5px solid #f0c64a;
        color: #f0c64a;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        font-weight: 700;
        font-size: 0.95rem;
        padding: 10px 22px;
        border-radius: 24px;
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.85);
        z-index: 99999;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.25s ease, transform 0.25s ease;
        text-align: center;
        max-width: 90vw;
      `;
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    requestAnimationFrame(() => {
      toast.style.opacity = '1';
      toast.style.transform = 'translateX(-50%) translateY(0)';
    });
    clearTimeout(GameReplayManager._toastTimer);
    GameReplayManager._toastTimer = setTimeout(() => {
      if (toast) {
        toast.style.opacity = '0';
        toast.style.transform = 'translateX(-50%) translateY(20px)';
      }
    }, duration);
  }

  /**
   * Convenience launcher: opens the replay of the game that just finished.
   */
  static playCurrentGameReplay() {
    const replay = GameReplayManager.lastRecordedReplay || GameReplayManager.loadReplays()[0];
    const repModal = (typeof window !== 'undefined' && window.ReplayModal) ||
                     (typeof globalThis !== 'undefined' && globalThis.ReplayModal) ||
                     (typeof window !== 'undefined' && window.CrazyCribbage?.ReplayModal);
    if (replay && repModal && typeof repModal.open === 'function') {
      repModal.open(replay);
      return;
    }
    GameReplayManager.showToast('No recorded game replay available yet.', 3000);
  }

  /**
   * Convenience sharer: triggers sharing dialog for the game that just finished.
   */
  static shareCurrentGameReplay() {
    const replay = GameReplayManager.lastRecordedReplay || GameReplayManager.loadReplays()[0];
    const repModal = (typeof window !== 'undefined' && window.ReplayModal) ||
                     (typeof globalThis !== 'undefined' && globalThis.ReplayModal) ||
                     (typeof window !== 'undefined' && window.CrazyCribbage?.ReplayModal);
    if (replay && repModal && typeof repModal.openShareDialog === 'function') {
      repModal.openShareDialog(replay);
      return;
    }
    GameReplayManager.showToast('No recorded game replay available to share.', 3000);
  }
}
